import { gql } from '@apollo/client';
import type { ParsedUrlQuery } from 'querystring';

import { frontConfig } from '../../config';
import axios from '../../services/axios';
import type { Graphdebate } from '../../types/api';
import type { ALLOWED_SORTS } from '../ThreadMessages/query';
import { AuthorFragment } from './thread';

export const TopicBodyFragment = gql`
  fragment TopicBodyFragment on Topic {
    id
    slug
    subject
    content
    hiddenEmbeds
    embeds
    format
    subtitle
    chapo
    lettrine
    hero {
      imageUrl
      image {
        id
      }
      caption
      credit
      alt
    }
    createdAt
    contentEditedAt
    pinned
    isFavorite
    userReport {
      createdAt
    }
    identifier
    status
    lastMessage {
      id
      createdAt
    }
    messagesCount
    votes {
      score
      user {
        id
      }
    }
    author {
      ...AuthorFragment
      ... on User {
        profile {
          bio
        }
      }
    }
    categories {
      id
      title
      description
      slug
      isAdsSafe
      status
      adult
    }
    themes {
      id
      title
      slug
    }
    entities {
      id
      title
      slug
    }
    dup
    group {
      id
    }
    authorNotification
  }
  ${AuthorFragment}
`;

export const TopicListItemActionsFragment = gql`
  fragment TopicListItemActionsFragment on Topic {
    id
    pinned
    isFavorite
  }
`;

export const TopicListItemFragment = gql`
  fragment TopicListItemFragment on Topic {
    id
    subject
    content @skip(if: $skipContent)
    embeds @skip(if: $skipContent)
    slug
    author {
      ...AuthorFragment
    }
    createdAt
    messagesCount
    lastMessage {
      id
      author {
        ...AuthorFragment
      }
      createdAt
    }
    categories {
      id
      slug
      title
    }
    themes {
      id
      title
      slug
    }
    entities {
      id
      title
      slug
    }
    votes {
      score
      user {
        id
      }
    }
    group {
      id
    }
    userReport {
      createdAt
    }
  }
  ${AuthorFragment}
`;

export const topicsFilterQuery = gql`
  query topicsFilterQuery(
    $page: Int
    $limit: Int
    $dateStart: GraphQLDateTime
    $dateEnd: GraphQLDateTime
    $filter: String
    $skipContent: Boolean = false
  ) {
    website {
      id
      name
      topicList(
        page: $page
        limit: $limit
        dateStart: $dateStart
        dateEnd: $dateEnd
        filter: $filter
      ) {
        meta {
          count
          page
          limit
        }
        topics {
          id
          ...TopicListItemFragment
          ...TopicListItemActionsFragment
        }
      }
    }
  }
  ${TopicListItemFragment}
  ${TopicListItemActionsFragment}
`;

export const topicsProfileQuery = gql`
  query topicsProfileQuery(
    $limit: Int
    $page: Int
    $nextPage: Int
    $pseudo: String
    $sort: String
    $skipContent: Boolean = false
  ) {
    profile(pseudo: $pseudo) {
      id
      topicList(page: $page, limit: $limit, sort: $sort) {
        meta {
          count
        }
        topics {
          id
          ...TopicListItemFragment
          status
          moderationMotifId
        }
      }
      nextTopicList: topicList(page: $nextPage, limit: $limit, sort: $sort) {
        topics {
          id
          ...TopicListItemFragment
          status
        }
      }
    }
  }
  ${TopicListItemFragment}
`;

export const TopicQuery = gql`
  query TopicQuery($slug: String) {
    website {
      id
      findTopic(slug: $slug) {
        id
        ...TopicBodyFragment
        findOrCreateThread(offset: 0, limit: 0) {
          id
          countMessages
          countRootMessages
        }
      }
    }
  }
  ${TopicBodyFragment}
`;

export const homeTopicsQuery = gql`
  query HomeTopicsQuery($limit: Int = 20, $sort: String, $skipContent: Boolean = false) {
    website {
      id
      name
      topicList(page: 1, limit: $limit, sort: $sort) {
        meta {
          count
        }
        topics {
          id
          ...TopicListItemFragment
        }
      }
    }
  }
  ${TopicListItemFragment}
`;

// --- Magazine "press" blocks (media mode, ticket B2_02) ---------------------
// Editorial articles need the press fields (hero/subtitle/format) on top of the
// list basics so the magazine cards can show a cover image + kicker.
export const PressTopicListItemFragment = gql`
  fragment PressTopicListItemFragment on Topic {
    id
    slug
    subject
    format
    subtitle
    pinned
    hero {
      imageUrl
      alt
      image {
        id
      }
    }
    createdAt
    messagesCount
    categories {
      id
      slug
      title
    }
    themes {
      id
      title
      slug
    }
    entities {
      id
      title
      slug
    }
    author {
      ...AuthorFragment
    }
  }
  ${AuthorFragment}
`;

// Recent editorial articles for the magazine home (À la une / Le fil / Le reste).
// NB: the API's pinned-float pass only applies to tag-scoped searches — press
// blocks send no tag, so ordering comes solely from $sort (hot/published).
// filter:"editorial" (2026-07-24) : les vitrines magazine ne servent QUE les
// articles de la rédaction (ceux qui ont un `format`) — un sujet de lecteur
// public reste visible partout ailleurs (forum, rubriques, recherche), jamais ici.
export const pressTopicsQuery = gql`
  query PressTopicsQuery($limit: Int = 12, $sort: String) {
    website {
      id
      topicList(page: 1, limit: $limit, sort: $sort, filter: "editorial") {
        meta {
          count
        }
        topics {
          id
          ...PressTopicListItemFragment
        }
      }
    }
  }
  ${PressTopicListItemFragment}
`;

// The website's default readers circle (Group.isDefault, exposed by B1_04).
export const defaultCircleQuery = gql`
  query DefaultCircleQuery {
    website {
      id
      groupSearch(status: "approved") {
        groups {
          id
          name
          slug
          isDefault
        }
      }
    }
  }
`;

// Latest topics scoped to a circle (the readers-circle access block).
// $skipContent is declared because TopicListItemFragment references it (@skip).
export const circleTopicsQuery = gql`
  query CircleTopicsQuery(
    $groupId: IDOrString
    $limit: Int = 12
    $sort: String
    $skipContent: Boolean = false
  ) {
    website {
      id
      topicList(page: 1, limit: $limit, groupId: $groupId, sort: $sort) {
        meta {
          count
        }
        topics {
          id
          ...TopicListItemFragment
        }
      }
    }
  }
  ${TopicListItemFragment}
`;

export const HOME_DEFAULT_SORT = 'relevance';
export const HOME_DEFAULT_LIMIT = 10;

export function makeHomeTopicsVariables(graphdebate: Graphdebate, query: ParsedUrlQuery) {
  if (!graphdebate) throw new Error('makeHomeTopicsVariables needs graphdebate which is falsy.');
  const { latestTopicsSortOrder, countPerListPage } = graphdebate;
  let sort0: ALLOWED_SORTS =
    (query.sort as ALLOWED_SORTS) || latestTopicsSortOrder || HOME_DEFAULT_SORT;
  // const sort = REDIRECT_SORT_API[sort0]; // If we need to map newest to newestLine for the API
  const sort = sort0;
  // Hack: 20 is the hard-coded max, until the back-office can provide a specific setting for the home page limit.
  // countPerListPage is a global setting that also impacts the tags pages.
  const limit = Math.min(countPerListPage || HOME_DEFAULT_LIMIT, 20);
  return { limit, sort };
}

export const adminDeleteTopic = ({
  motifId,
  motif,
  pageId,
}: {
  pageId: string | number;
  motifId?: string | number;
  motif?: string;
}) => {
  if (motifId === undefined) {
    const errorMsg = 'Cannot delete an admin topic without a motif_id.';
    frontConfig.IS_DEV && console.error(errorMsg);
    throw new Error(errorMsg);
  }
  return axios.put(`/api/page/${pageId}/status`, {
    status: 'deleted',
    motif_id: motifId,
    motif,
  });
};

export const bookmarkTopic = async (pageId: string, newfavoriteValue: boolean) => {
  const { data } = await axios.post(`/api/page/${pageId}/favorite`, {
    favorite: newfavoriteValue,
  });

  return data;
};

export const deleteTopic = ({ pageId }: { pageId: string | number }) =>
  axios.delete(`/api/page/${pageId}`);

export const pinTopic = async ({ pageId, unPin }: { pageId: string; unPin?: boolean }) => {
  const { data } = await axios.post(`/api/page/${pageId}/pinned`, { pinned: !unPin });

  return data;
};

export const GetTopicQuery = gql`
  query GetTopicQuery($slug: String, $hasSlug: Boolean!) {
    me {
      profile {
        getGroups {
          groups {
            id
            name
            isDefault
            readersHeading
          }
        }
      }
    }
    website @include(if: $hasSlug) {
      id
      findTopic(slug: $slug) {
        id
        ...TopicBodyFragment
      }
    }
  }
  ${TopicBodyFragment}
`;

export const CreateOrUpdateTopicMutation = gql`
  mutation CreateOrUpdateTopicMutation(
    $topicId: ID
    $subject: String!
    $content: String!
    $hiddenEmbeds: [String]
    $embeds: String
    $categoryIds: [ID]
    $authorNotification: Boolean
    $groupId: ID
    $adminBoost: Boolean
    $adminBoostValue: Int
    $guestUserName: String
    $guestEmail: String
    $fileIds: [ID]
    $format: String
    $subtitle: String
    $chapo: String
    $heroFileId: ID
    $heroImageUrl: String
    $heroCaption: String
    $heroCredit: String
    $lettrine: Boolean
    $themeIds: [ID]
    $entityIds: [ID]
  ) {
    createOrUpdateTopic(
      topicId: $topicId
      subject: $subject
      content: $content
      hiddenEmbeds: $hiddenEmbeds
      embeds: $embeds
      categoryIds: $categoryIds
      authorNotification: $authorNotification
      groupId: $groupId
      adminBoost: $adminBoost
      adminBoostValue: $adminBoostValue
      guestUserName: $guestUserName
      guestEmail: $guestEmail
      fileIds: $fileIds
      format: $format
      subtitle: $subtitle
      chapo: $chapo
      heroFileId: $heroFileId
      heroImageUrl: $heroImageUrl
      heroCaption: $heroCaption
      heroCredit: $heroCredit
      lettrine: $lettrine
      themeIds: $themeIds
      entityIds: $entityIds
    ) {
      id
      ...TopicBodyFragment
    }
  }
  ${TopicBodyFragment}
`;
